import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSystemProblem,
  LocationAlerts,
  queryKeys,
  resolveSystemProblem,
  SystemProblem,
  SystemProblemDashboardResponse,
  useBusyAction,
} from '@vgrubs/components';
import { replaceSystemProblem } from './useSystemProblemsSocket';

/**
 * One problem, for the detail modal.
 *
 * Seeded from whatever the list, the Dashboard card or a location's alerts
 * already know, so the
 * modal opens with its content rather than a spinner. The socket writes into this same key, so a
 * colleague resolving it while it is open changes what is on screen.
 */
export const useSystemProblem = (problemId: number | null) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.systemProblems.detail(problemId ?? 0),
    queryFn: async () => {
      const { data } = await getSystemProblem(problemId as number);
      return data.data;
    },
    enabled: problemId !== null,
    placeholderData: () =>
      [
        ...queryClient
          .getQueriesData<SystemProblemDashboardResponse>({
            queryKey: queryKeys.systemProblems.dashboard,
          })
          .flatMap(([, card]) => card?.items ?? []),
        ...queryClient
          .getQueriesData<{ pages: Array<{ items: SystemProblem[] }> }>({
            queryKey: queryKeys.systemProblems.listAll,
          })
          .flatMap(([, data]) => data?.pages ?? [])
          .flatMap((page) => page.items),
        ...queryClient
          .getQueriesData<LocationAlerts>({
            queryKey: queryKeys.locationInsights.alertsAll,
          })
          .flatMap(([, alerts]) => alerts?.systemProblems ?? []),
      ].find((problem) => problem.id === problemId),
  });

  return { problem: query.data ?? null, isLoading: query.isLoading };
};

/**
 * Marking a problem resolved. The answer is the whole problem, applied to the
 * detail and every list showing it, and the Dashboard card refetches.
 *
 * A 400 means somebody else resolved it first, so a failure refreshes rather
 * than leaving the button offering it again.
 */
export const useResolveSystemProblem = (problemId: number | null) => {
  const queryClient = useQueryClient();
  const { busy, run } = useBusyAction();

  const resolve = async () => {
    if (problemId === null) return false;
    const ok = await run(async () => {
      const { data } = await resolveSystemProblem(problemId);
      replaceSystemProblem(queryClient, data.data);
    }, 'Marked as resolved.');
    if (!ok) {
      queryClient.invalidateQueries({ queryKey: queryKeys.systemProblems.all });
    }
    return ok;
  };

  return { busy, resolve };
};
